import { Category, ExtendedLedgerDataItem, LedgerData, LedgerDataItem, MessageType, PartialLedgerDataItem, Subcategory } from "../types";
import { formatDate, dollarFormat, getEntryTypeName } from "./format";
import { setToUTC } from './date';
import { MONTH_HEIGHT } from "../constants/theme";

export const addLedgerDataItem = (ledgerData: LedgerData, addedItem: ExtendedLedgerDataItem, startingBalance: number): LedgerData => {
  let updatedLedgerData = JSON.parse(JSON.stringify(ledgerData));
  updatedLedgerData.items.push(addedItem);
  updatedLedgerData.items = sortLedgerData(updatedLedgerData);
  updatedLedgerData.items = updateItemBalances(updatedLedgerData, startingBalance);

  return updatedLedgerData;
};

export const deleteLedgerDataItem = (ledgerData: LedgerData, deletedItem: PartialLedgerDataItem, startingBalance: number): LedgerData => {
  let updatedLedgerData = JSON.parse(JSON.stringify(ledgerData));
  const index = ledgerData.items.findIndex((item) => {
    return item.guid === deletedItem.guid;
  });

  if (index >= 0) {
    updatedLedgerData.items.splice(index, 1);
    updatedLedgerData.items = sortLedgerData(updatedLedgerData);
    updatedLedgerData.items = updateItemBalances(updatedLedgerData, startingBalance);
  } else {
    console.error('No match was found for the updated item in the ledger data', deletedItem);
  }

  return updatedLedgerData;
};

export const getCategoryNameByGuid = (guid: string, categories: Array<Category>): string => {
  return categories.find((category: Category) => { return category.guid === guid; })?.label || '';
};

export const getSubcategoryNameByGuid = (guid: string, subcategories: Array<Subcategory>): string => {
  return subcategories.find((subcategory: Subcategory) => { return subcategory.guid === guid; })?.label || '';
};

export const getLedgerDataItemByGuid = (ledgerData: LedgerData, itemGuid: string): LedgerDataItem | undefined => {
  const index = ledgerData.items.findIndex((item) => {
    return item.guid === itemGuid;
  });

  if (index >= 0) {
    return ledgerData.items[index];
  } else {
    console.error('No match was found for the item in the ledger data', itemGuid);
    return undefined;
  }
};

export const getMessage = (messageType: MessageType, item: PartialLedgerDataItem) => {
  const itemDate = formatDate(item.settledDate, 'MMM. D, YYYY');
  const itemType = getEntryTypeName(item.type_id).toLowerCase();

  switch(messageType) {
    case MessageType.CONFIRM_CATEGORIZATION:
      return `Please select categories for '${item.label}' for ${dollarFormat(item.amount)} on ${itemDate}.`;
    case MessageType.CONFIRM_DELETE:
      return `Are you sure you would like to delete the ${itemDate}, transaction '${item.label}' for ${dollarFormat(item.amount)}? This action cannot be undone.`;
    case MessageType.ITEM_CATEGORIZED:
      return `Successfully categorized ${itemType} '${item.label}' from ${itemDate}`;
    case MessageType.ITEM_DELETED:
      return `Successfully deleted ${itemType} '${item.label}' from ${itemDate}`;
    case MessageType.ITEM_ADDED:
      return `Successfully added ${itemType} '${item.label}' from ${itemDate}`;
    case MessageType.ITEM_EDITED:
      return `Successfully updated ${itemType} '${item.label}' from ${itemDate}`;
    case MessageType.CONFIRM_MULTIPLE_EDIT:
      return `Are you sure you would like to update all unpaid, visible transactions to '${item.label}' for ${dollarFormat(item.amount)}? This action cannot be undone.`;
    case MessageType.CONFIRM_DELETE_ITEMS:
      return `Are you sure you would like to delete all items from ${formatDate(item.settledDate, 'YYYY')}? This action cannot be undone.`;
    default:
      return ``;
  }
};

export const allLedgerItemsArePaid = (items: Array<ExtendedLedgerDataItem>): boolean => {
  return items.every((item: ExtendedLedgerDataItem) => { return !!item.paid });
};

export const getHighlightedNavTop = (percentScrolled: number, navHeight: number): number => {
  return Math.min((percentScrolled / 100) * navHeight, navHeight - (navHeight * MONTH_HEIGHT));
};

export const getHighlightedNavHeight = (percentScrolled: [number, number], navHeight: number): number => {
  return (percentScrolled[1] - percentScrolled[0]) * navHeight / 100;
};

export const noLedgerItemsArePaid = (items: Array<ExtendedLedgerDataItem>): boolean => {
  return items.every((item: ExtendedLedgerDataItem) => { return !item.paid });
};

export const netValue = (amount: number, typeId: number): number => {
  return typeId === 1 ? amount : -amount;
};

export const sortLedgerData = (ledgerData: LedgerData): Array<ExtendedLedgerDataItem> => {
  const sortedLedgerData = JSON.parse(JSON.stringify(ledgerData)) as LedgerData;
  // DB sort is ORDER BY "settledDate" ASC, paid DESC, type_id ASC, amount ASC
  sortedLedgerData.items.sort((a, b) => {
    if (a.settledDate < b.settledDate) {
      return -1;
    } else if (a.settledDate > b.settledDate) {
      return 1;
    } else if (a.paid && !b.paid) {
      return -1;
    } else if (a.type_id === 1 && (b.type_id === 3 || b.type_id === 2)) {
      return -1;
    } else if (a.type_id === 2 && b.type_id === 1) {
      return 1;
    } else if (a.type_id === 2 && b.type_id === 3) {
      return -1;
    } else if (a.type_id === 3 && b.type_id === 2) {
      return 1;
    } else if (a.type_id === 3 && b.type_id === 1) {
      return 1;
    } else if (a.amount > b.amount) {
      return -1;
    } else if (a.amount < b.amount) {
      return 1;
    } else {
      return 0;
    }
  });

  return sortedLedgerData.items;
};

export const updateItemBalances = (ledgerData: LedgerData, startingBalance: number): Array<ExtendedLedgerDataItem> => {
  let balance = startingBalance;

  return ledgerData.items.map((item: ExtendedLedgerDataItem, index: number) => {
    balance += netValue(Number(ledgerData.items[index].amount), ledgerData.items[index].type_id);
    return Object.assign(item, { balance, starting_balance: startingBalance ? startingBalance : item.starting_balance });
  });
};

export const updateItemCategories = (ledgerData: LedgerData, categories: Array<Category>, subcategories: Array<Subcategory>): Array<ExtendedLedgerDataItem> => {
  return ledgerData.items.map((item: ExtendedLedgerDataItem) => {
    const categoryName: string = getCategoryNameByGuid(item.category_guid!, categories);
    const subcategoryName: string = getSubcategoryNameByGuid(item.subcategory_guid!, subcategories);
    return Object.assign(item, { categoryName, subcategoryName });
  });
};

export const updateLedgerDataItem = (ledgerData: LedgerData, updatedItem: PartialLedgerDataItem, startingBalance: number): LedgerData => {
  let updatedLedgerData = JSON.parse(JSON.stringify(ledgerData));
  const index = ledgerData.items.findIndex((item) => {
    return item.guid === updatedItem.guid;
  });

  if (index >= 0) {
    updatedLedgerData.items[index].settledDate = setToUTC(updatedItem.settledDate);
    updatedLedgerData.items[index].type_id = updatedItem.type_id;
    updatedLedgerData.items[index].amount = updatedItem.amount;
    updatedLedgerData.items[index].paid = updatedItem.paid;
    updatedLedgerData.items[index].label = updatedItem.label;
    updatedLedgerData.items[index].category_guid = updatedItem.category_guid;
    updatedLedgerData.items[index].subcategory_guid = updatedItem.subcategory_guid;
    updatedLedgerData.items = sortLedgerData(updatedLedgerData);
    updatedLedgerData.items = updateItemBalances(updatedLedgerData, startingBalance);
  } else {
    console.error('No match was found for the updated item in the ledger data', updatedItem);
  }

  return updatedLedgerData;
};
